import React, { useState, useRef } from 'react';
import { FormControlLabel, FormHelperText, Switch, TextField } from '@mui/material';
import { FormControl, Grid, InputLabel, MenuItem, Select } from '@mui/material';
import { useSelector } from 'react-redux';
import { validateISRC } from './helperUPCISRC';

export default function AddMusicDetailsModal({
    data,
    compareId,
    updateSong,
    createSong,
    genre,
    moodList,
    languageList
}) {
    const userData = useSelector((state) => state.userData.userData);
    const isDisabled = data.is_admin_created;

    const [errors, setErrors] = useState({});
    const [musicDetailData, setMusicDetailData] = useState({
        genre: data?.genre?.id || "",
        mood: data?.mood?.id || "",
        language: data?.language?.id || "",
        artists: data?.artists || '',
        isrc: data?.isrc || '',
        // These four are on the DETAIL serializer only, so `data` carries them
        // just when the caller has fetched the song by id. On a row straight
        // from the song list they are absent, and default to empty rather than
        // to `undefined` — an uncontrolled input that later becomes controlled
        // is a React warning and a cursor that jumps.
        about: data?.about || '',
        release_date: data?.release_date || '',
        record_label: data?.record_label || '',
        is_explicit: Boolean(data?.is_explicit)
    });

    // keep initial values reference for comparison
    const initialValues = useRef(musicDetailData);

    function onChangeHandler(e) {
        const { name, value } = e.target;
        setMusicDetailData((prev) => ({
            ...prev,
            [name]: value
        }));
    }

    const validateFields = (fieldName, fieldValue) => {
        const newErrors = {};
        // individual validation for efficiency
        if (fieldName === 'artists') {
            if (fieldValue && fieldValue.trimStart().length !== fieldValue.length) {
                newErrors[fieldName] = 'Start and End space not allowed';
            } else if (fieldValue.length > 20) {
                newErrors[fieldName] = 'The artist name should not exceed 20 characters.';
            }
        }

        if (fieldName === 'isrc') {
            const isrcValidation = validateISRC(fieldValue);
            if (fieldValue && isrcValidation.isValid) {
                newErrors[fieldName] = isrcValidation.errorMessage;
            }
        }

        if (fieldName === 'record_label') {
            if (fieldValue && fieldValue.length > 255) {
                newErrors[fieldName] = 'The record label should not exceed 255 characters.';
            }
        }

        if (fieldName === 'release_date') {
            // The column is a DateField, so anything the browser cannot parse is
            // rejected server-side. <input type="date"> already emits YYYY-MM-DD;
            // this catches a hand-typed value in the browsers that allow one.
            if (fieldValue && !/^\d{4}-\d{2}-\d{2}$/.test(fieldValue)) {
                newErrors[fieldName] = 'Use a full date.';
            }
        }

        if (['genre', 'mood', 'language'].includes(fieldName)) {
            if (!fieldValue) {
                newErrors[fieldName] = `${fieldName} is required`;
            }
        }

        return newErrors;
    };

    // Validate one field and, if it passes, save the whole detail object.
    // Extracted from handleBlur because a Switch has no meaningful blur — the
    // toggle IS the edit, and waiting for focus to leave would lose it.
    function commit(name, value) {
        // skip API call if no change
        if (initialValues.current[name] === value) return;

        // validate only this field
        const fieldErrors = validateFields(name, value);

        if (Object.keys(fieldErrors).length === 0) {
            // no errors → call API
            if (userData.id && compareId === undefined) {
                createSong({ musicDetailData: { ...musicDetailData, [name]: value } });
            } else {
                updateSong({ musicDetailData: { ...musicDetailData, [name]: value } });
            }
            // update reference value after successful change
            initialValues.current = {
                ...initialValues.current,
                [name]: value
            };
            setErrors((prev) => ({ ...prev, [name]: undefined }));
        } else {
            setErrors((prev) => ({ ...prev, ...fieldErrors }));
        }
    }

    function handleBlur(e) {
        const { name, value } = e.target;
        commit(name, value);
    }

    function handleToggle(e) {
        const { name, checked } = e.target;
        setMusicDetailData((prev) => ({ ...prev, [name]: checked }));
        commit(name, checked);
    }

    const SelectField = [
        { mt: 2, id: "genre", label: "Select Genre", value: musicDetailData.genre, name: 'genre', listData: genre, requiredField: true, disabled: isDisabled },
        { mt: 2, id: "mood", label: "Select Mood", value: musicDetailData.mood, name: 'mood', listData: moodList, requiredField: true, disabled: isDisabled },
        { mt: 0, id: "language", label: "Select Language", value: musicDetailData.language, name: 'language', listData: languageList, requiredField: true, disabled: isDisabled },
    ];

    const inputField = [
        { id: "artists", label: "Artist Name", value: musicDetailData.artists, name: 'artists', requiredField: true, disabled: isDisabled },
        { id: "isrc", label: "ISRC", value: musicDetailData.isrc, name: 'isrc', requiredField: false, disabled: isDisabled },
        { id: "record_label", label: "Record Label", value: musicDetailData.record_label, name: 'record_label', requiredField: false, disabled: isDisabled },
        // A date input needs `shrink`: the placeholder "dd/mm/yyyy" the browser
        // draws sits where an un-shrunk MUI label would, and the two overlap.
        { id: "release_date", label: "Release Date", value: musicDetailData.release_date, name: 'release_date', requiredField: false, disabled: isDisabled, type: 'date', shrink: true },
    ];

    return (
        <div>
            {/* -------------------- Artist,Genre, Mood and Language ---------------------- */}
            <Grid container spacing={2}>
                {inputField.map((d, i) => (
                    <Grid key={i} item xs={12} sm={6} md={6} lg={6}>
                        <TextField
                            className='w-100'
                            id={d.id}
                            name={d.name}
                            label={d.label}
                            value={d.value}
                            type={d.type || 'text'}
                            InputLabelProps={d.shrink ? { shrink: true } : undefined}
                            onChange={onChangeHandler}
                            onBlur={handleBlur}
                            error={!!errors[d.name]}
                            helperText={errors[d.name]}
                            required={d.requiredField}
                            disabled={d.disabled}
                            size='small'
                        />
                    </Grid>
                ))}
            </Grid>

            <Grid container spacing={2}>
                {SelectField.map((d, i) => (
                    <Grid key={i} item xs={12} sm={6} md={6} lg={6} mt={d.mt}>
                        <FormControl fullWidth size='small' required={d.requiredField} error={!!errors[d.name]} disabled={d.disabled}>
                            <InputLabel id={d.id}>{d.label}</InputLabel>
                            <Select
                                labelId={d.id}
                                id={d.id}
                                name={d.name}
                                label={d.label}
                                value={d.value}
                                onChange={onChangeHandler}
                                onBlur={handleBlur}
                                required={d.requiredField}
                                disabled={d.disabled}
                            >
                                {d?.listData.map(v => {
                                    const displayValue = d.name === "genre" ? v.genre : d.name === "language" ? v.language : d.name === "mood" ? v.mood : '';
                                    const displayId = d.name === "genre" ? v.id : d.name === "language" ? v.id : d.name === "mood" ? v.id : '';
                                    return <MenuItem key={displayId} value={displayId}>{displayValue}</MenuItem>;
                                })}
                            </Select>
                            <FormHelperText>{errors[d.name]}</FormHelperText>
                        </FormControl>
                    </Grid>
                ))}

                {/* The empty half-cell the three selects leave behind. The
                    toggle is the only other one-line control on the form, so it
                    is the one thing that fits there. */}
                <Grid item xs={12} sm={6} md={6} lg={6} mt={0} sx={{ display: 'flex', alignItems: 'center' }}>
                    <FormControlLabel
                        control={
                            <Switch
                                name="is_explicit"
                                checked={Boolean(musicDetailData.is_explicit)}
                                onChange={handleToggle}
                                disabled={isDisabled}
                            />
                        }
                        label={<span style={{ fontSize: 13 }}>Contains explicit content</span>}
                    />
                </Grid>
            </Grid>

            {/* -------------------- About and content warning ---------------------- */}
            <Grid container spacing={2} mt={0}>
                <Grid item xs={12}>
                    <TextField
                        placeholder="What is the story behind this track?"
                        className='w-100'
                        id="about"
                        name="about"
                        label="About this song"
                        value={musicDetailData.about}
                        onChange={onChangeHandler}
                        onBlur={handleBlur}
                        error={!!errors.about}
                        helperText={
                            errors.about
                            || 'The story behind the track. Also the fallback description on search results when you set no custom one.'
                        }
                        disabled={isDisabled}
                        size='small'
                        multiline
                        rows={4}
                    />
                </Grid>
            </Grid>
        </div>
    );
}
